/**
 * Sentinel Thread (middle column)
 * The full conversation thread. Each turn shows the customer query, then a single
 * "AI vs Agent" card (matching the Conversations page): Kim's response on top
 * with its verdict badge, the human agent's actual reply collapsible below (only
 * on earlier turns — the latest turn is Kim-only). A per-response feedback button
 * sits to the card's left; selecting it opens the form in the right panel.
 * Administrators additionally get delete / in-place edit controls on each Kim
 * response and a delete-conversation action in the header.
 */

import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, Check, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MarkdownContent } from "@/components/markdown-content";
import { VerdictBadge } from "@/modules/sentinel/verdict-badge";
import { SentinelResponseEditor } from "@/modules/sentinel/sentinel-response-editor";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import type {
  SentinelConversationHeader,
  SentinelThreadTurn,
} from "@/services/sentinelReview.service";

/** Administrator response management. Absent for every other role. */
export interface SentinelManageActions {
  isBusy: boolean;
  /** Resolve true once the conversation's Sentinel responses are gone. */
  onDeleteConversation: () => Promise<boolean>;
  onDeleteResponse: (sentinelMessageId: number) => Promise<boolean>;
  onEditResponse: (sentinelMessageId: number, content: string) => Promise<boolean>;
}

interface SentinelThreadProps {
  header: SentinelConversationHeader | null;
  turns: SentinelThreadTurn[];
  selectedMessageId: number | null;
  isLoading: boolean;
  /** Client Feedback page: view-only feedback buttons. */
  readOnly?: boolean;
  manage?: SentinelManageActions;
  onSelectResponse: (sentinelMessageId: number) => void;
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "MMM d, h:mm a");
}

function initialsOf(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name || email || "?").trim();
  return source.slice(0, 2).toUpperCase();
}

export function SentinelThread({
  header,
  turns,
  selectedMessageId,
  isLoading,
  readOnly = false,
  manage,
  onSelectResponse,
}: SentinelThreadProps) {
  const [confirmConversationDelete, setConfirmConversationDelete] = useState(false);
  const title =
    header?.subject?.trim() || header?.contact?.name || (header ? `Conversation #${header.conversationId}` : "");
  const ticketId = header?.externalId ?? header?.displayId ?? null;

  // "Response N" numbers Kim's responses, so turns where Kim never drafted one
  // are skipped in the count — otherwise the labels would jump (1, 3, 4) as soon
  // as the thread includes exchanges that were never sent to review.
  const responseNumbers = useMemo(() => {
    const numbers = new Map<number, number>();
    let count = 0;
    for (const turn of turns) {
      if (turn.sentinelMessage) numbers.set(turn.customerMessage.id, ++count);
    }
    return numbers;
  }, [turns]);

  return (
    <div className="flex h-full min-w-0 flex-col bg-muted/20">
      <div className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-6">
        <div className="min-w-0">
          <h1 className="line-clamp-1 text-base font-semibold text-foreground">{title}</h1>
          {header?.contact?.email && (
            <p className="line-clamp-1 text-xs text-muted-foreground">{header.contact.email}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {ticketId && (
            <span className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
              #{ticketId}
            </span>
          )}
          {manage && header && (
            <IconAction label="Delete conversation" onClick={() => setConfirmConversationDelete(true)}>
              <Trash2 className="size-4" />
            </IconAction>
          )}
        </div>
      </div>

      {manage && (
        <ConfirmDialog
          open={confirmConversationDelete}
          title="Delete this conversation?"
          description="Every Kim response in it is removed, along with any feedback left on them. The customer and agent messages stay. This cannot be undone."
          confirmLabel="Delete conversation"
          destructive
          isPending={manage.isBusy}
          onConfirm={async () => {
            if (await manage.onDeleteConversation()) setConfirmConversationDelete(false);
          }}
          onCancel={() => setConfirmConversationDelete(false)}
        />
      )}

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex max-w-3xl flex-col gap-8 px-6 py-8">
          {turns.length === 0 ? (
            <div className="py-20 text-center text-sm text-muted-foreground">
              {isLoading ? "Loading…" : "Select a conversation to review its responses."}
            </div>
          ) : (
            turns.map((turn) => (
              <ThreadTurn
                key={turn.customerMessage.id}
                turn={turn}
                responseNumber={responseNumbers.get(turn.customerMessage.id)}
                contactName={header?.contact?.name}
                contactEmail={header?.contact?.email}
                isSelected={turn.sentinelMessage != null && turn.sentinelMessage.id === selectedMessageId}
                readOnly={readOnly}
                manage={manage}
                onSelect={() => {
                  if (turn.sentinelMessage) onSelectResponse(turn.sentinelMessage.id);
                }}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function ThreadTurn({
  turn,
  responseNumber,
  contactName,
  contactEmail,
  isSelected,
  readOnly,
  manage,
  onSelect,
}: {
  turn: SentinelThreadTurn;
  /** Absent when Kim drafted no response for this turn — there is nothing to number. */
  responseNumber?: number;
  contactName?: string | null;
  contactEmail?: string | null;
  isSelected: boolean;
  readOnly: boolean;
  manage?: SentinelManageActions;
  onSelect: () => void;
}) {
  const [agentOpen, setAgentOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const sentinelMessage = turn.sentinelMessage;

  // Review page: the latest turn is the exchange in play, so it shows Kim only —
  // no agent comparison, even when Kim drafted nothing for it. Earlier turns show
  // both. Client Feedback views completed conversations, so the agent section
  // always renders there — with the reply, or a "No agent reply" placeholder.
  const renderAgent = readOnly || (!turn.isLatest && turn.agentMessage != null);

  // With neither side to show, render the customer query alone rather than an
  // empty card — the case where the latest turn has no Kim draft.
  const hasResponseCard = sentinelMessage != null || renderAgent;

  return (
    <div className="flex flex-col gap-4">
      {/* Customer query — incoming, left */}
      <div className="flex max-w-[85%] items-start gap-3 self-start">
        <Avatar className="mt-1 size-8 shrink-0">
          <AvatarFallback className="text-xs">{initialsOf(contactName, contactEmail)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="mb-1 text-xs font-medium text-muted-foreground">{contactName || "Customer"}</p>
          <div className="rounded-2xl rounded-tl-sm border border-border bg-muted px-4 py-3">
            <MarkdownContent
              text={turn.customerMessage.content}
              className="text-xs leading-normal text-foreground"
            />
            <p className="mt-2 text-right text-xs text-muted-foreground">
              {formatTimestamp(turn.customerMessage.createdAt)}
            </p>
          </div>
        </div>
      </div>

      {/* AI vs Agent card — outgoing, right, with the feedback button on the left.
          The feedback column only appears when there is a Kim response to score. */}
      {hasResponseCard && (
      <div className="flex items-start justify-end gap-3">
        {sentinelMessage && (
          <div className="flex min-w-[92px] shrink-0 flex-col items-center gap-1.5 pt-3">
            <span className="text-xs text-muted-foreground">Response {responseNumber}</span>
            <FeedbackButton
              reviewed={turn.review != null}
              addressed={turn.review?.addressed ?? false}
              readOnly={readOnly}
              isSelected={isSelected}
              onClick={onSelect}
            />
          </div>
        )}

        <div
          className={cn(
            "min-w-0 max-w-md flex-1 overflow-hidden rounded-[20px] border bg-card transition-colors",
            isSelected ? "border-foreground/40" : "border-border",
          )}
        >
          {/* Kim's response */}
          {sentinelMessage && (
            <div className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-violet-600">Kim's Response</p>
                <div className="flex items-center gap-1">
                  {turn.review ? (
                    <VerdictBadge verdict={turn.review.verdict} />
                  ) : (
                    <span className="text-xs text-muted-foreground">Awaiting review</span>
                  )}
                  {manage && !isEditing && (
                    <>
                      <IconAction label="Edit response" onClick={() => setIsEditing(true)}>
                        <Pencil className="size-3.5" />
                      </IconAction>
                      <IconAction label="Delete response" onClick={() => setConfirmDelete(true)}>
                        <Trash2 className="size-3.5" />
                      </IconAction>
                    </>
                  )}
                </div>
              </div>
              {manage && isEditing ? (
                <SentinelResponseEditor
                  messageId={sentinelMessage.id}
                  initialMarkdown={sentinelMessage.content ?? ""}
                  isSaving={manage.isBusy}
                  onSave={async (markdown) => {
                    if (await manage.onEditResponse(sentinelMessage.id, markdown)) setIsEditing(false);
                  }}
                  onCancel={() => setIsEditing(false)}
                />
              ) : (
                <MarkdownContent
                  text={sentinelMessage.content}
                  className="text-xs leading-normal text-foreground"
                />
              )}
              {manage && (
                <ConfirmDialog
                  open={confirmDelete}
                  title="Delete this response?"
                  description="Kim's response and any feedback left on it are removed. This cannot be undone."
                  confirmLabel="Delete response"
                  destructive
                  isPending={manage.isBusy}
                  onConfirm={async () => {
                    if (await manage.onDeleteResponse(sentinelMessage.id)) setConfirmDelete(false);
                  }}
                  onCancel={() => setConfirmDelete(false)}
                />
              )}
            </div>
          )}

          {/* Agent's actual reply (comparison). Collapsible; shows a placeholder
              when the turn has no public agent reply. */}
          {renderAgent && (
            <>
              {sentinelMessage && <div className="border-t border-border" />}
              <Collapsible open={agentOpen} onOpenChange={setAgentOpen}>
                <div className="flex flex-col gap-3 p-4">
                  <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between">
                    <p className="text-xs font-medium text-foreground">Agent's Response</p>
                    <ChevronDown
                      className={cn(
                        "size-4 text-muted-foreground transition-transform",
                        agentOpen ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {turn.agentMessage ? (
                      <MarkdownContent
                        text={turn.agentMessage.content}
                        className="text-xs leading-normal text-muted-foreground"
                      />
                    ) : (
                      <p className="text-xs italic leading-normal text-muted-foreground">No agent reply for this turn.</p>
                    )}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </>
          )}

          {/* Timestamp footer — the Kim draft's time, or the agent reply's on a
              turn Kim never drafted for. */}
          <div className="border-t border-border" />
          <div className="flex justify-end px-4 py-2.5">
            <span className="text-xs text-muted-foreground">
              {formatTimestamp((sentinelMessage ?? turn.agentMessage)?.createdAt ?? null)}
            </span>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

/** Small icon-only control for the administrator actions. */
function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {children}
    </button>
  );
}

function FeedbackButton({
  reviewed,
  addressed,
  readOnly,
  isSelected,
  onClick,
}: {
  reviewed: boolean;
  addressed: boolean;
  readOnly: boolean;
  isSelected: boolean;
  onClick: () => void;
}) {
  // Read-only (Client Feedback): nothing to view on un-reviewed responses.
  if (readOnly && !reviewed) {
    return <span className="shrink-0 text-xs text-muted-foreground">No feedback</span>;
  }

  const label = readOnly ? "View feedback" : reviewed ? "Edit feedback" : "Give feedback";

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex shrink-0 items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer",
        isSelected
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-background text-secondary-foreground hover:bg-muted",
      )}
    >
      {readOnly && addressed && <Check className="size-3 text-green-foreground" />}
      {label}
    </button>
  );
}
